"use client";

import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, Info, X, CircleCheck } from "lucide-react";
import React, { createContext, useContext, useReducer, ReactNode } from "react";
import { v4 as uuid } from "uuid";

import { cn } from "@/dashboard/lib/utils";

export type BannerVariant = "success" | "info" | "error";
type BannerTtl = number | "permanent";
type BannerOpts = { id?: string; ttl?: BannerTtl };

export interface Banner {
  id: string; // unique key so React can animate/remove
  variant: BannerVariant;
  message: string | ReactNode;
  ttl?: BannerTtl; // ms until auto-dismiss (undefined = stay until closed)
}

/* ---------- Actions ---------- */
type Action = { type: "ADD"; banner: Banner } | { type: "REMOVE"; id: string };

function reducer(state: Banner[], action: Action): Banner[] {
  switch (action.type) {
    case "ADD":
      return state.some(b => b.id === action.banner.id)
        ? state // already present → ignore
        : [...state, action.banner];
    case "REMOVE":
      return state.filter(b => b.id !== action.id);
    default:
      return state;
  }
}

/* ---------- Context ---------- */
interface BannerContextValue {
  banners: Banner[];
  dispatch: React.Dispatch<Action>;
  // convenience helpers
  success: (msg: Banner["message"], opts?: BannerOpts) => void;
  info: (msg: Banner["message"], opts?: BannerOpts) => void;
  error: (msg: Banner["message"], opts?: BannerOpts) => void;
}

const BannerContext = createContext<BannerContextValue | undefined>(undefined);

export function BannerProvider({ children }: { children: ReactNode }) {
  const [banners, dispatch] = useReducer(reducer, []);

  const add =
    (variant: BannerVariant) =>
    (message: Banner["message"], opts: BannerOpts = {}) => {
      const id = opts.id ?? uuid();
      let ttl = opts.ttl;

      if (ttl === undefined) {
        if (variant === "info" || variant === "success") {
          ttl = 4000;
        }
      } else if (ttl === "permanent") {
        ttl = undefined;
      }

      dispatch({ type: "ADD", banner: { id, variant, message, ttl } });
      if (ttl) {
        setTimeout(() => dispatch({ type: "REMOVE", id }), ttl);
      }
    };

  const value: BannerContextValue = {
    banners,
    dispatch,
    success: add("success"),
    info: add("info"),
    error: add("error"),
  };

  return (
    <BannerContext.Provider value={value}>
      {children}
      <BannerStack /> {/* renders banners at top of page */}
    </BannerContext.Provider>
  );
}

/* ---------- Hook ---------- */
export function useBanner() {
  const ctx = useContext(BannerContext);
  if (!ctx) {
    throw new Error("useBanner must be used inside <BannerProvider>");
  }
  return ctx;
}

/* ---------- Renderer ---------- */
function BannerStack() {
  const { banners, dispatch } = useBanner();
  if (banners.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed z-50 flex flex-col gap-2 pointer-events-none",
        "p-4 sm:p-6",
        "top-0 right-0 sm:items-end", // top-right on ≥sm
        "items-center w-full sm:w-auto" // center on xs
      )}
    >
      <AnimatePresence>
        {banners.map(b => (
          <motion.div
            key={b.id}
            layout
            initial={{ opacity: 0, x: 100, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.96 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 35,
              duration: 0.4,
            }}
            className={cn(
              "bg-background pointer-events-auto w-full sm:w-80 md:w-96",
              "rounded-lg shadow-lg px-4 py-3 text-sm flex items-center gap-3"
            )}
          >
            <div className="shrink-0">
              {b.variant === "success" && <CircleCheck className="h-5 w-5 text-success" />}
              {b.variant === "info" && <Info className="h-5 w-5 text-accent" />}
              {b.variant === "error" && <AlertCircle className="h-5 w-5 text-destructive" />}
            </div>
            <span className="flex-1 text-foreground">{b.message}</span>
            <button
              onClick={() => dispatch({ type: "REMOVE", id: b.id })}
              className="shrink-0 opacity-70 hover:opacity-100"
            >
              <span className="sr-only">Close</span>
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
